/**
 * Fan-out GA4 ecommerce events to pixels already loaded in public/deferred-trackers.js.
 * Do not add the same pixels as GTM tags or conversions will double-count.
 * Meta: browser fbq + POST /internal/meta-capi (shared eventID).
 * Snap: browser snaptr + POST /internal/snap-capi (client_dedup_id = event_id).
 * TikTok: browser ttq + POST /internal/tiktok-capi (shared event_id).
 * X: browser twq; page view comes from twq("config") in deferred-trackers.js,
 *    purchase carries conversion_id = transaction_id so X dedupes it.
 * Purchase is also sent server-side when Odoo confirms the order (server/purchase-server-events.mjs),
 * with the same order-number event id on every platform.
 */

const PIXEL_RETRY_MS = 400;
const PIXEL_RETRY_MAX = 25;
const META_CAPI_PATH = "/internal/meta-capi";
const SNAP_CAPI_PATH = "/internal/snap-capi";
const TIKTOK_CAPI_PATH = "/internal/tiktok-capi";
const PURCHASE_CONTEXT_PATH = "/internal/purchase-context";
const SNAP_CLICK_KEY = "tazej_snap_sccid";
const TIKTOK_CLICK_KEY = "tazej_ttclid";
const X_CLICK_KEY = "tazej_twclid";
const META_PIXEL_ID = "1091319379999967";
/** Set once Meta advanced matching is applied (also by public/deferred-trackers.js). */
const META_AM_FLAG = "__tazejMetaAm";
const SNAP_PIXEL_ID = "8b78fb53-23bd-4aed-bc9e-ef81f5503282";

const TIKTOK_EVENTS = {
    page_view: "Pageview",
    view_item: "ViewContent",
    select_item: "ViewContent",
    add_to_cart: "AddToCart",
    view_cart: "ViewContent",
    begin_checkout: "InitiateCheckout",
    add_payment_info: "AddPaymentInfo",
    purchase: "CompletePayment",
    sign_up: "CompleteRegistration",
    add_to_wishlist: "AddToWishlist",
};

const SNAP_EVENTS = {
    page_view: "PAGE_VIEW",
    view_item: "VIEW_CONTENT",
    select_item: "VIEW_CONTENT",
    add_to_cart: "ADD_CART",
    view_cart: "VIEW_CONTENT",
    begin_checkout: "START_CHECKOUT",
    add_shipping_info: "ADD_BILLING",
    add_payment_info: "ADD_BILLING",
    purchase: "PURCHASE",
    sign_up: "SIGN_UP",
    login: "LOGIN",
    add_to_wishlist: "ADD_TO_WISHLIST",
};

/** Meta Pixel standard events — https://developers.facebook.com/docs/meta-pixel/reference */
const META_EVENTS = {
    page_view: "PageView",
    view_item: "ViewContent",
    select_item: "ViewContent",
    add_to_cart: "AddToCart",
    view_cart: "ViewContent",
    begin_checkout: "InitiateCheckout",
    add_payment_info: "AddPaymentInfo",
    purchase: "Purchase",
    sign_up: "CompleteRegistration",
    add_to_wishlist: "AddToWishlist",
};

/** X event IDs from the X Ads events manager (pixel nxfdu). */
const X_EVENTS = {
    view_item: "tw-nxfdu-rfyqy",
    select_item: "tw-nxfdu-rfyqy",
    view_cart: "tw-nxfdu-rfyqy",
    add_to_cart: "tw-nxfdu-rfyr8",
    begin_checkout: "tw-nxfdu-rfyrg",
    purchase: "tw-nxfdu-oreut",
};

function whenPixelsReady(cb, attempt = 0) {
    if (typeof window === "undefined") {
        return;
    }
    const fbqReady = typeof window.fbq === "function";
    const snapReady = typeof window.snaptr === "function";
    const ttqReady = typeof window.ttq?.track === "function" || typeof window.ttq === "function";
    if ((fbqReady && snapReady && ttqReady) || attempt >= PIXEL_RETRY_MAX) {
        cb();
        return;
    }
    setTimeout(() => whenPixelsReady(cb, attempt + 1), PIXEL_RETRY_MS);
}

function contentsFromItems(items = []) {
    return items.map((item) => ({
        content_id: String(item.item_id || ""),
        content_name: String(item.item_name || ""),
        quantity: Number(item.quantity) || 1,
        price: Number(item.price) || 0,
    }));
}

function metaContentsFromItems(items = []) {
    return items.map((item) => ({
        id: String(item.item_id || ""),
        quantity: Number(item.quantity) || 1,
        item_price: Number(item.price) || 0,
    }));
}

function xContentsFromItems(items = []) {
    return items.map((item) => ({
        content_type: "product",
        content_id: String(item.item_id || ""),
        content_name: String(item.item_name || ""),
        content_price: Number(item.price) || 0,
        num_items: Number(item.quantity) || 1,
    }));
}

function xEmail(email) {
    const trimmed = typeof email === "string" ? email.trim().toLowerCase() : "";
    return trimmed.includes("@") ? trimmed : undefined;
}

/** X expects E.164 (+9665XXXXXXXX). */
function xPhone(phone) {
    let digits = String(phone || "").replace(/\D/g, "");
    if (digits.startsWith("00966")) {
        digits = digits.slice(2);
    } else if (digits.startsWith("05")) {
        digits = "966" + digits.slice(1);
    } else if (digits.startsWith("5") && digits.length === 9) {
        digits = "966" + digits;
    }
    return digits.startsWith("966") ? `+${digits}` : undefined;
}

function readCookie(name) {
    if (typeof document === "undefined") {
        return "";
    }
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : "";
}

function newEventId(prefix) {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return `${prefix}_${crypto.randomUUID()}`;
    }
    return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function resolveSharedEventId(prefix, transactionId) {
    if (transactionId) {
        return String(transactionId);
    }
    return newEventId(prefix);
}

function splitPersonName(name) {
    const parts = String(name || "")
        .replace(/\s+/g, " ")
        .trim()
        .split(" ")
        .filter(Boolean);
    if (!parts.length) {
        return { firstName: undefined, lastName: undefined };
    }
    return {
        firstName: parts[0],
        lastName: parts.slice(1).join(" ") || undefined,
    };
}

function readJsonStorage(key) {
    try {
        const raw = localStorage.getItem(key);
        const parsed = raw ? JSON.parse(raw) : null;
        return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
        return {};
    }
}

function cityFromValue(value) {
    if (!value) {
        return undefined;
    }
    if (typeof value === "string" && value.trim()) {
        return value.trim();
    }
    if (typeof value === "object" && value.name) {
        return String(value.name).trim() || undefined;
    }
    return undefined;
}

function resolveAdsUser(extra = {}) {
    const stored = readJsonStorage("userData");
    const area = readJsonStorage("selectedAria");
    const userData = extra.user_data && typeof extra.user_data === "object" ? extra.user_data : {};
    const name = extra.name || extra.first_name || userData.name || stored.name;
    const split = splitPersonName(name);
    const firstName = extra.first_name || extra.fn || split.firstName;
    const lastName = extra.last_name || extra.ln || split.lastName;
    const city =
        cityFromValue(extra.city) ||
        cityFromValue(userData.city) ||
        cityFromValue(area.name ? area : extra.geo_city);
    const zip = extra.zip || extra.postal_code || extra.zp || stored.zip || undefined;
    const country = extra.country || "sa";
    const externalId = extra.external_id || extra.externalId || stored.id || userData.id;
    return {
        email: extra.email || userData.email || stored.email || undefined,
        phone:
            extra.phone ||
            extra.phone_number ||
            userData.phone_number ||
            userData.phone ||
            stored.phone ||
            undefined,
        firstName: firstName || undefined,
        lastName: lastName || undefined,
        city: city || undefined,
        zip: zip || undefined,
        country: country || undefined,
        externalId: externalId != null && String(externalId) !== "" ? String(externalId) : undefined,
    };
}

function identifyPixels(user) {
    if (!user) {
        return;
    }
    // fbevents applies advanced matching only on the first init that carries user data; later inits just warn.
    if (typeof window.fbq === "function" && !window[META_AM_FLAG]) {
        const am = {};
        if (user.email) am.em = user.email;
        if (user.phone) am.ph = user.phone;
        if (user.firstName) am.fn = user.firstName;
        if (user.lastName) am.ln = user.lastName;
        if (user.city) am.ct = user.city;
        if (user.zip) am.zp = user.zip;
        if (user.country) am.country = user.country;
        if (user.externalId) am.external_id = user.externalId;
        if (am.em || am.ph || am.external_id) {
            window.fbq("init", META_PIXEL_ID, am);
            window[META_AM_FLAG] = true;
        }
    }
    if (typeof window.snaptr === "function") {
        const snapUser = {};
        if (user.email) snapUser.user_email = user.email;
        if (user.phone) snapUser.user_phone_number = user.phone;
        if (user.firstName) snapUser.firstname = user.firstName;
        if (user.lastName) snapUser.lastname = user.lastName;
        if (user.city) snapUser.geo_city = user.city;
        if (user.zip) snapUser.geo_postal_code = user.zip;
        if (user.country) snapUser.geo_country = user.country;
        window.snaptr("init", SNAP_PIXEL_ID, snapUser);
    }
    if (typeof window.ttq?.identify === "function") {
        const ttUser = {};
        if (user.email) ttUser.email = user.email;
        if (user.phone) ttUser.phone_number = user.phone;
        if (user.externalId) ttUser.external_id = user.externalId;
        if (Object.keys(ttUser).length) {
            window.ttq.identify(ttUser);
        }
    }
}

function adsUserPayload(user) {
    return {
        email: user.email,
        phone: user.phone,
        first_name: user.firstName,
        last_name: user.lastName,
        city: user.city,
        zip: user.zip,
        country: user.country,
        external_id: user.externalId,
    };
}

function captureSnapClickId() {
    if (typeof window === "undefined") {
        return "";
    }
    try {
        const fromUrl = new URLSearchParams(window.location.search).get("ScCid");
        if (fromUrl) {
            sessionStorage.setItem(SNAP_CLICK_KEY, fromUrl);
            return fromUrl;
        }
        return sessionStorage.getItem(SNAP_CLICK_KEY) || "";
    } catch {
        return "";
    }
}

function captureTikTokClickId() {
    if (typeof window === "undefined") {
        return "";
    }
    try {
        const fromUrl = new URLSearchParams(window.location.search).get("ttclid");
        if (fromUrl) {
            sessionStorage.setItem(TIKTOK_CLICK_KEY, fromUrl);
            return fromUrl;
        }
        return sessionStorage.getItem(TIKTOK_CLICK_KEY) || "";
    } catch {
        return "";
    }
}

export function captureXClickId() {
    if (typeof window === "undefined") {
        return "";
    }
    try {
        const fromUrl = new URLSearchParams(window.location.search).get("twclid");
        if (fromUrl) {
            sessionStorage.setItem(X_CLICK_KEY, fromUrl);
            return fromUrl;
        }
        return sessionStorage.getItem(X_CLICK_KEY) || readCookie("_twclid") || "";
    } catch {
        return "";
    }
}

/**
 * Save the buyer's ad identifiers on the server at order creation, so the Purchase that Odoo
 * triggers on confirmation can still be matched when the browser never returns (Apple Pay redirect).
 */
export function postPurchaseContext({ orderId, items = [], externalId } = {}) {
    if (typeof window === "undefined" || typeof fetch !== "function" || orderId == null) {
        return;
    }
    const body = {
        order_id: String(orderId),
        fbp: readCookie("_fbp") || undefined,
        fbc: readCookie("_fbc") || undefined,
        sc_cookie1: readCookie("_scid") || undefined,
        sc_click_id: captureSnapClickId() || undefined,
        ttclid: captureTikTokClickId() || undefined,
        ttp: readCookie("_ttp") || undefined,
        twclid: captureXClickId() || undefined,
        external_id: externalId != null && String(externalId) !== "" ? String(externalId) : undefined,
        event_source_url: window.location.href,
        items: (Array.isArray(items) ? items : []).map((item) => ({
            item_id: String(item?.item_id || ""),
            item_name: String(item?.item_name || ""),
            quantity: Number(item?.quantity) || 1,
            price: Number(item?.price) || 0,
        })),
    };
    try {
        fetch(PURCHASE_CONTEXT_PATH, {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify(body),
            keepalive: true,
        }).catch(() => {});
    } catch {
        /* ignore */
    }
}

function postMetaCapi({ eventName, eventId, params, transactionId, user = {} }) {
    if (typeof fetch !== "function") {
        return;
    }
    const body = {
        event_name: eventName,
        event_id: eventId,
        event_time: Math.floor(Date.now() / 1000),
        event_source_url: typeof window !== "undefined" ? window.location.href : "",
        fbp: readCookie("_fbp"),
        fbc: readCookie("_fbc"),
        ...adsUserPayload(user),
        custom_data: {
            value: params.value,
            currency: params.currency,
            content_type: params.content_type,
            contents: params.contents,
            content_ids: params.content_ids,
            num_items: params.num_items,
            order_id: transactionId ? String(transactionId) : undefined,
            transaction_id: transactionId ? String(transactionId) : undefined,
        },
    };
    try {
        fetch(META_CAPI_PATH, {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify(body),
            keepalive: true,
        }).catch(() => {});
    } catch {
        /* ignore */
    }
}

function postSnapCapi({ eventName, eventId, params, transactionId, user = {} }) {
    if (typeof fetch !== "function") {
        return;
    }
    const body = {
        event_name: eventName,
        event_id: eventId,
        event_time: Math.floor(Date.now() / 1000),
        event_source_url: typeof window !== "undefined" ? window.location.href : "",
        sc_cookie1: readCookie("_scid"),
        sc_click_id: captureSnapClickId() || undefined,
        ...adsUserPayload(user),
        custom_data: {
            value: params.price,
            currency: params.currency,
            content_ids: params.item_ids,
            num_items: params.number_items,
            order_id: transactionId ? String(transactionId) : undefined,
            transaction_id: transactionId ? String(transactionId) : undefined,
        },
    };
    try {
        fetch(SNAP_CAPI_PATH, {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify(body),
            keepalive: true,
        }).catch(() => {});
    } catch {
        /* ignore */
    }
}

function postTikTokCapi({ eventName, eventId, params, transactionId, user = {} }) {
    if (typeof fetch !== "function") {
        return;
    }
    const body = {
        event_name: eventName,
        event_id: eventId,
        event_time: Math.floor(Date.now() / 1000),
        event_source_url: typeof window !== "undefined" ? window.location.href : "",
        ttclid: captureTikTokClickId() || undefined,
        ttp: readCookie("_ttp") || undefined,
        ...adsUserPayload(user),
        custom_data: {
            value: params.value,
            currency: params.currency,
            content_type: params.content_type,
            contents: params.contents,
            order_id: transactionId ? String(transactionId) : undefined,
            transaction_id: transactionId ? String(transactionId) : undefined,
        },
    };
    try {
        fetch(TIKTOK_CAPI_PATH, {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify(body),
            keepalive: true,
        }).catch(() => {});
    } catch {
        /* ignore */
    }
}

export function fanOutAdPixels(eventName, ecommerce = {}, extra = {}) {
    if (typeof window === "undefined") {
        return;
    }

    whenPixelsReady(() => {
        const value = Number(ecommerce.value) || 0;
        const currency = ecommerce.currency || extra.currency || "SAR";
        const items = Array.isArray(ecommerce.items) ? ecommerce.items : [];
        const contents = contentsFromItems(items);
        const contentIds = contents.map((c) => c.content_id).filter(Boolean);
        const quantity = contents.reduce((sum, c) => sum + c.quantity, 0);
        const transactionId = extra.transaction_id || ecommerce.transaction_id;
        const user = resolveAdsUser(extra);
        identifyPixels(user);

        const ttName = TIKTOK_EVENTS[eventName];
        if (ttName) {
            const eventId = resolveSharedEventId(ttName, transactionId);
            const params = {
                value,
                currency,
                contents,
                content_type: "product",
            };
            if (transactionId) {
                params.order_id = String(transactionId);
            }
            if (ttName === "Pageview") {
                if (typeof window.ttq?.page === "function") {
                    window.ttq.page();
                } else if (typeof window.ttq?.track === "function") {
                    window.ttq.track(ttName, {}, { event_id: eventId });
                }
            } else if (typeof window.ttq?.track === "function") {
                window.ttq.track(ttName, params, { event_id: eventId });
            }
            postTikTokCapi({
                eventName: ttName,
                eventId,
                params: ttName === "Pageview" ? {} : params,
                transactionId,
                user,
            });
        }

        const snapName = SNAP_EVENTS[eventName];
        if (snapName) {
            const eventId = resolveSharedEventId(snapName, transactionId);
            const params = {
                price: value,
                currency,
                item_ids: contentIds,
                number_items: quantity,
                client_dedup_id: eventId,
            };
            if (transactionId) {
                params.transaction_id = String(transactionId);
            }
            if (user.email) {
                params.user_email = user.email;
            }
            if (user.phone) {
                params.user_phone_number = user.phone;
            }
            if (user.firstName) {
                params.firstname = user.firstName;
            }
            if (user.lastName) {
                params.lastname = user.lastName;
            }
            if (user.city) {
                params.geo_city = user.city;
            }
            if (user.zip) {
                params.geo_postal_code = user.zip;
            }
            if (user.country) {
                params.geo_country = user.country;
            }
            if (typeof window.snaptr === "function") {
                window.snaptr("track", snapName, params);
            }
            postSnapCapi({
                eventName: snapName,
                eventId,
                params,
                transactionId,
                user,
            });
        }

        const metaName = META_EVENTS[eventName];
        if (metaName) {
            const params = {
                value,
                currency,
                content_type: "product",
                contents: metaContentsFromItems(items),
                content_ids: contentIds,
                num_items: quantity,
            };
            const eventId = resolveSharedEventId(metaName, transactionId);
            if (typeof window.fbq === "function") {
                window.fbq("track", metaName, params, { eventID: eventId });
            }
            postMetaCapi({
                eventName: metaName,
                eventId,
                params,
                transactionId,
                user,
            });
        }

        const xEventId = X_EVENTS[eventName];
        if (xEventId && typeof window.twq === "function") {
            const params = { value, currency };
            if (items.length) {
                params.contents = xContentsFromItems(items);
            }
            if (transactionId) {
                params.conversion_id = String(transactionId);
            }
            const email = xEmail(user.email);
            if (email) {
                params.email_address = email;
            }
            const phone = xPhone(user.phone);
            if (phone) {
                params.phone_number = phone;
            }
            window.twq("event", xEventId, params);
        }
    });
}
